import { useNavigation } from '@react-navigation/native';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import actions, { back } from '../../actions';
import {
    addProviderError,
    batchChangeConsent
} from '../../actions/consents';
import { getConsentData } from '../../agreementsConfig';
import {
    Button,
    ButtonsGroup,
    Page,
    Platform,
    Title,
    TopNotification,
    View
} from '../../common';
import { cleanString } from '../../common/Agreemants';
import { HyperLink } from '../../common/Hyperlink.js';
import { SHOW_CONSENTS_AS_TEXT } from '../../config.js';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    STORE_PROFILE_CHILD,
    usePersonalData
} from '../../hooks/usePersonalData';
import {
    getConsents,
    getProviders
} from '../../selectors/consents';
import { getProviderCapabilities } from '../../selectors/providerCodes';
import { openURL } from '../../services/device.js';
import userServices from '../../services/user.js';
import { getHasLocalVisits } from '../../services/user.utils';
import { buttonsStyles } from '../../styles/button.js';
import { capitalize } from '../../utils';
import {
    CLINIC_CONSENT_KEYS,
    getClinicsConsents,
    initializeClinicsConsentsState,
    shouldShowConsentCheckbox
} from '../../utils/consentsUtils';
import { useBackHandler } from '../../utils/routing';
import { getSupportBody } from '../../utils/supportInfo.js';
import { renderRelativePanel } from '../Account/snippets.js';
import { StyledAgreementCheckbox } from '../Visits/snippets/NewVisitModal/snippets.js';
import { ClinicConsentsGroup } from './ClinicConsentsGroup';
import {
    ConsentRetractionModal,
    shouldShowConsentRetractionModal
} from './ConsentRetractionModal';

const ProfileItem = renderRelativePanel(false);

const ChildProfileCard = ({ item }) => {
    return (
        <View style={{
            marginBottom: 16,
            width: '100%',
        }}>
            {item?.name && item?.birthday ? (
                <ProfileItem
                    item={item}
                    withEditBadge={false}
                    readOnly
                    hideMenu
                />
            ) : null}
        </View>
    );
};

export const ChildConsent = ({
    profileId,
    onConsentUpdateComplete,
}) => {
    const {
        consents,
        dispatch,
        isLoading,
        familyObject,
    } = usePersonalData();
    const { providerErrors } = useSelector(getConsents);
    const [ offlineClinics, setOfflineClinics ] = useState([]);

    const [ error, setError ] = useState(null);

    // Get child profile data from familyObject
    const childProfile = familyObject?.[profileId];

    const providers = useSelector(getProviders);

    // Get child consents from the consents structure
    const childConsents = consents?.family?.[profileId] || {};

    const shouldShowStoreProfileChild = shouldShowConsentCheckbox(childConsents?.main?.[STORE_PROFILE_CHILD]);
    const shouldShowMedicalDataHistory = shouldShowConsentCheckbox(childConsents?.main?.[CHILD_HISTORY_KEY]);
    const policeLinkTitle = Object.R('titles.consentsPolicy');

    const [ storeProfileChildChecked, setStoreProfileChildChecked ] = useState(childConsents?.main?.[STORE_PROFILE_CHILD] || false);
    const [ medicalDataHistoryChecked, setMedicalDataHistoryChecked ] = useState(childConsents?.main?.[CHILD_HISTORY_KEY] || false);

    // Get clinic data from consents.family[profileId]
    const providerCapabilities = useSelector(getProviderCapabilities);
    const clinicsConsents = getClinicsConsents(consents?.family?.[profileId], CLINIC_CONSENT_KEYS.CHILD, false, providerCapabilities);
    const [ clinicsConsentsState, setClinicsConsentsState ] = useState(
        initializeClinicsConsentsState(clinicsConsents),
    );

    // Check if at least one clinic consent should be shown
    const shouldShowClinicsSection = clinicsConsents.some(clinic => clinic.shouldShow);

    // Optional consents should be shown only if STORE_PROFILE_CHILD is true in this session
    const shouldShowOptionalConsents = storeProfileChildChecked;

    const navigation = useNavigation();
    const canLeave = useRef(false);

    const prevProfileIdRef = useRef();

    // Keep main checkboxes in sync with the current child's server state (including after switching child)
    useEffect(() => {
        const rawStore = childConsents?.main?.[STORE_PROFILE_CHILD];
        setStoreProfileChildChecked(rawStore === true || rawStore === false ? rawStore : false);
    }, [ profileId, childConsents?.main ]);

    useEffect(() => {
        const rawHistory = childConsents?.main?.[CHILD_HISTORY_KEY];
        setMedicalDataHistoryChecked(rawHistory === true || rawHistory === false ? rawHistory : false);
    }, [ profileId, childConsents?.main ]);

    useEffect(() => {
        if (!consents?.family?.[profileId]) {
            return;
        }
        const newClinicsConsents = getClinicsConsents(consents.family[profileId], CLINIC_CONSENT_KEYS.CHILD, false, providerCapabilities);
        const newClinicsState = initializeClinicsConsentsState(newClinicsConsents);

        const prevId = prevProfileIdRef.current;
        const switchedChild = prevId !== undefined && prevId !== profileId;
        prevProfileIdRef.current = profileId;

        if (switchedChild) {
            setClinicsConsentsState(newClinicsState);
            setOfflineClinics([]);
            return;
        }

        // Same child: merge with current state so Redux refetches do not wipe in-progress selections
        setClinicsConsentsState((prevState) => {
            const mergedState = { ...newClinicsState };
            Object.keys(prevState).forEach((key) => {
                if (prevState[key] === true) {
                    mergedState[key] = true;
                }
            });
            return mergedState;
        });
    }, [ consents?.family,
        profileId,
        providerCapabilities ]);

    // Reset optional consents when STORE_PROFILE_CHILD is unchecked
    useEffect(() => {
        if (!storeProfileChildChecked) {
            setMedicalDataHistoryChecked(false);
            setClinicsConsentsState((prevState) => {
                const resetState = {};
                Object.keys(prevState).forEach(key => {
                    resetState[key] = false;
                });
                return resetState;
            });
        }
    }, [ storeProfileChildChecked ]);

    useBackHandler(() => true);

    // Prevent "back" navigation on iOS via swipe gesture to enforce navigation only through buttons
    useEffect(() => {
        navigation.setOptions({
            gestureEnabled: false,
        });
    }, [ navigation ]);

    useEffect(() => {
        return navigation.addListener('beforeRemove', (e) => {
            if (!canLeave.current) {
                e.preventDefault();
            }
        });
    }, [ navigation ]);

    // Handler for clinic checkbox change
    const handleClinicConsentChange = (providerCode, newValue) => {
        setClinicsConsentsState((prevState) => ({
            ...prevState,
            [providerCode]: newValue,
        }));
        console.log(`Clinic consent changed: ${providerCode} = ${newValue}`);
    };

    const onSelect = () => {
        // TODO: implement onSelect for policy link
    };

    const onLogout = async () => {
        canLeave.current = true;
        Object.profilesWithoutConsent = null;
        back();
        await actions.logOut();
        if (Object.callbackOnSignIn) {
            Object.callbackOnSignIn = null;
            back();
        }
    };

    const onConfirm = async () => {
        const hasVisits = await getHasLocalVisits(profileId);

        // Check if modal should be shown based on consent values
        const showModal = shouldShowConsentRetractionModal(
            childConsents?.main,
            CHILD_HISTORY_KEY,
            CHILD_CLINIC_HISTORY_KEY,
            clinicsConsents,
            clinicsConsentsState,
            true, // isChildProfile
            STORE_PROFILE_CHILD,
            medicalDataHistoryChecked, // current value from UI
            storeProfileChildChecked, // current value from UI
            hasVisits,
        );

        if (!showModal) {
            // If modal shouldn't be shown, proceed directly to consent update
            handleConsentUpdate();
            return;
        }

        // Determine which blocks should be shown based on current state
        const showStoreProfileChildBlock = shouldShowStoreProfileChild && !storeProfileChildChecked;
        const showMedicalDataHistoryBlock = shouldShowOptionalConsents && shouldShowMedicalDataHistory && !medicalDataHistoryChecked;
        const showClinicBlock = shouldShowOptionalConsents && shouldShowClinicsSection &&
            clinicsConsents.some(clinic => clinic.shouldShow && !clinicsConsentsState[clinic.providerCode]);

        // Get unchecked clinics
        const uncheckedClinics = clinicsConsents
            .filter(clinic => clinic.shouldShow && !clinicsConsentsState[clinic.providerCode])
            .filter(({ providerCode } = {}) => !providerErrors?.includes?.(providerCode))
            .map(clinic => ({
                name: clinic.name || clinic.providerCode,
                providerCode: clinic.providerCode,
            }));

        // If any block should be shown, display confirmation modal
        if (showStoreProfileChildBlock || showMedicalDataHistoryBlock || showClinicBlock) {
            Page.showModal(
                <ConsentRetractionModal
                    profile={{
                        name: childProfile?.name || '',
                        birthday: childProfile?.birthday || '',
                    }}
                    uncheckedClinics={uncheckedClinics}
                    showMedicalDataHistoryBlock={showMedicalDataHistoryBlock}
                    showClinicBlock={showClinicBlock}
                    showChildStorageBlock={showStoreProfileChildBlock}
                    onConfirm={() => {
                        Page.closeModal();
                        handleConsentUpdate();
                    }}
                    onCancel={Page.closeModal}
                />,
            );
        } else {
            // If no blocks to show, proceed with consent update directly
            handleConsentUpdate();
        }
    };

    const handleConsentUpdate = useCallback(async () => {
        // Collect only consents that previously had no value (were undefined/null)
        const batchConsents = [];

        // Check STORE_PROFILE_CHILD
        if (shouldShowStoreProfileChild) {
            batchConsents.push({type: STORE_PROFILE_CHILD, provided: storeProfileChildChecked});
        }
        if (!storeProfileChildChecked) {
            console.log('No STORE_PROFILE_CHILD: user will be deleted: ', profileId);
            userServices.deleteChildSimplified(profileId);
        }

        // Check MEDICAL_DATA_HISTORY_CHILD (only if optional consents should be shown)
        if (shouldShowOptionalConsents && shouldShowMedicalDataHistory) {
            batchConsents.push({type: CHILD_HISTORY_KEY, provided: medicalDataHistoryChecked});
        }

        // Check MEDICAL_DATA_HISTORY_CHILD_CLINIC for clinics (only if optional consents should be shown)
        const clinicConsents = {};
        if (shouldShowOptionalConsents) {
            clinicsConsents.forEach((clinic) => {
                const providerCode = clinic?.providerCode;
                if (clinic.shouldShow) {
                    if (!clinicConsents[providerCode]) {
                        clinicConsents[providerCode] = {};
                    }
                    batchConsents.push({
                        type: CHILD_CLINIC_HISTORY_KEY,
                        provided: clinicsConsentsState[providerCode] || false,
                        providerCode,
                    });
                    clinicConsents[providerCode][CHILD_CLINIC_HISTORY_KEY] = clinicsConsentsState[providerCode] || false;
                }
            });
        }

        console.log('changedConsents: ', batchConsents);
        console.log('profileId: ', profileId);

        const allClinics = Object.keys(clinicConsents);
        const offline = allClinics.filter(clinic => offlineClinics.includes(clinic));

        if (offline?.length) {
            dispatch(addProviderError(offline));
        }

        dispatch(batchChangeConsent({profileId, consents: batchConsents, onComplete: (result) => {
            const errors = result?.filter?.(r => r.status === 'error') || [];
            if (errors.length) {
                const errorClinics = errors.map(e => e.providerCode);
                setOfflineClinics(errorClinics);
            } else {
                setError(null);
                canLeave.current = true;
                Object.profilesWithoutConsent = null;
                onConsentUpdateComplete?.(profileId, offline);
            }}, onError: e => {
            try {
                setError({
                    ...e,
                    'level': error
                });
            } catch {
                setError(e);
            }
        }}));


    }, [clinicsConsents,
        clinicsConsentsState,
        dispatch,
        error,
        medicalDataHistoryChecked,
        offlineClinics,
        onConsentUpdateComplete,
        profileId,
        shouldShowMedicalDataHistory,
        shouldShowOptionalConsents,
        shouldShowStoreProfileChild,
        storeProfileChildChecked]);

    const clinics = useMemo(() => {
        const result = offlineClinics?.map(c => providers?.[c]?.name ?? c);
        return result?.join(', ') || '';
    }, [ offlineClinics, providers ]);

    const clinicError = useMemo(() => {
        return offlineClinics?.length ? {
            level: 'error',
            message: capitalize(Object.R('errors.ErrorSetClinicConsent', {
                consent: capitalize(getConsentData(CHILD_HISTORY_KEY)?.textWithoutPrefix ?? ''),
                clinic: clinics,
            })),
        } : '';
    }, [ offlineClinics, clinics ]);

    const onSubmitError = useCallback(() => {
        openURL(`mailto:${Object.R('supportEmail')}${getSupportBody()}`);
    }, []);


    return (
        <>
            <View style={{
                alignItems: 'center',
                marginTop: Platform.OS === 'ios' ? 60 : 40,
                marginBottom: 20,
                width: '100%',
            }}>
                <Title bold>{(Object.R('titles.usingTheApplication')).toUpperCase()}</Title>
                <Title
                    numberOfLines={null}
                    style={{
                        marginTop: 12,
                        marginBottom: 24,
                        width: '100%',
                        color: '#9C9C9C',
                    }}
                >
                    {Object.R('titles.consentsSecondarySubtitle')}
                </Title>
                <ChildProfileCard item={childProfile}/>
                {shouldShowStoreProfileChild ? (
                    <StyledAgreementCheckbox
                        titleComponent={getConsentData(STORE_PROFILE_CHILD)?.TextData}
                        selected={storeProfileChildChecked}
                        onPress={() => setStoreProfileChildChecked(!storeProfileChildChecked)}
                        containerStyle={{ marginTop: 0 }}
                        checkboxStyle={{
                            marginBottom: 12,
                            marginRight: 12,
                        }}
                    />
                ) : null}
                {shouldShowOptionalConsents ? (
                    <>
                        {shouldShowMedicalDataHistory || shouldShowClinicsSection ? <Title
                            numberOfLines={null}
                            style={{
                                color: '#9C9C9C',
                                marginTop: 12,
                                width: '100%',
                            }}>
                            {Object.R('titles.consentsOptionalSubtitle')}
                        </Title> : null}
                        {shouldShowClinicsSection ? (
                            <>
                                <Title
                                    numberOfLines={null}
                                    style={{ marginTop: 12,
                                        width: '100%',}}
                                >
                                    {Object.R('titles.ChildConsentsRevokeMDCTitle')}
                                </Title>
                                <ClinicConsentsGroup
                                    clinicsConsents={clinicsConsents}
                                    clinicsConsentsState={clinicsConsentsState}
                                    onClinicConsentChange={handleClinicConsentChange}
                                    disabledClinics={offlineClinics}
                                />
                            </>
                        ) : null}
                        {shouldShowMedicalDataHistory ? (
                            <StyledAgreementCheckbox
                                titleComponent={getConsentData(CHILD_HISTORY_KEY)?.TextData}
                                selected={medicalDataHistoryChecked}
                                onPress={() => setMedicalDataHistoryChecked(!medicalDataHistoryChecked)}
                                checkboxStyle={{
                                    marginBottom: 12,
                                    marginRight: 12,
                                }}
                                containerStyle={{
                                    marginTop: 16,
                                    marginBottom: 6,
                                }}
                            />
                        ) : null}
                    </>
                ) : null}
            </View>
            {(shouldShowMedicalDataHistory || shouldShowClinicsSection) && clinicError ? <TopNotification
                level="error"
                hint={clinicError}
                containerStyle={{
                    borderBottomWidth: 0,
                    borderTopWidth: 1,
                    marginTop: 8,
                    marginBottom: 0,
                    paddingBottom: 0,
                }}
            /> : null}
            <View>
                <Title
                    style={{
                        // marginHorizontal: 12,
                        marginVertical: 12,
                    }}
                >
                    <HyperLink
                        title={SHOW_CONSENTS_AS_TEXT === '1' ? cleanString(policeLinkTitle) : policeLinkTitle}
                        style={{
                            linkColor: buttonsStyles.link.color,
                            color: '#555555',
                        }}
                        linkStyle={{
                            textDecorationLine: 'underline',
                        }}
                        onClickText={onSelect}
                    />
                </Title>
                {error && (
                    <TopNotification
                        level="error"
                        hint={error}
                        containerStyle={{
                            borderBottomWidth: 0,
                            borderTopWidth: 1,
                            marginTop: 8,
                            marginBottom: 0,
                            paddingBottom: 0,
                        }}
                        submitText={Object.R('buttons.writeToSupport')}
                        submitContainerStyle={{
                            borderWidth: 0,
                            alignSelf: 'flex-end',
                            marginRight: 16,
                            marginBottom: 12,
                        }}
                        onSubmit={onSubmitError}
                    />
                )}
                <ButtonsGroup
                    style={{
                        paddingBottom: 12,
                        borderTopWidth: 1,
                        borderTopColor: '#F0F0F0',
                    }}
                >
                    <Button
                        transparent
                        action={onLogout}
                        title="buttons.exit"
                        titleStyles={{
                            fontSize: 14,
                            fontWeight: '900',
                            fontFamily: 'SourceSansPro-Semibold',
                        }}
                    />
                    <Button
                        primary
                        action={onConfirm}
                        title="buttons.continue"
                        disabled={isLoading}
                        style={{
                            fontSize: 14,
                            fontWeight: '900',
                            fontFamily: 'SourceSansPro-Semibold',
                        }}
                    />
                </ButtonsGroup>
            </View>
        </>
    );
};
